import React, { useRef, useEffect, useState } from 'react';
import { cn } from '@/utils/cn';

export interface ScrollAreaProps {
  children: React.ReactNode;
  className?: string;
  orientation?: 'vertical' | 'horizontal' | 'both';
  scrollHideDelay?: number;
  thumbClassName?: string;
  trackClassName?: string;
}

export const ScrollArea = React.forwardRef<HTMLDivElement, ScrollAreaProps>(
  (
    {
      children,
      className,
      orientation = 'vertical',
      scrollHideDelay = 600,
      thumbClassName,
      trackClassName,
      ...props
    },
    ref
  ) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [_isScrolling, setIsScrolling] = useState(false);
    const [_showVerticalBar, setShowVerticalBar] = useState(false);
    const [_showHorizontalBar, setShowHorizontalBar] = useState(false);
    const scrollTimeout = useRef<NodeJS.Timeout>();

    useEffect(() => {
      const scrollElement = scrollRef.current;
      if (!scrollElement) return;

      const checkScrollbars = () => {
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = scrollElement;

        if (orientation === 'vertical' || orientation === 'both') {
          setShowVerticalBar(scrollHeight > clientHeight);
        }

        if (orientation === 'horizontal' || orientation === 'both') {
          setShowHorizontalBar(scrollWidth > clientWidth);
        }
      };

      const handleScroll = () => {
        setIsScrolling(true);

        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
        }

        scrollTimeout.current = setTimeout(() => {
          setIsScrolling(false);
        }, scrollHideDelay);
      };

      checkScrollbars();
      scrollElement.addEventListener('scroll', handleScroll);

      // Check scrollbars on resize
      const resizeObserver = new ResizeObserver(checkScrollbars);
      resizeObserver.observe(scrollElement);

      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
        }
      };
    }, [orientation, scrollHideDelay]);

    const getScrollbarStyle = (type: 'vertical' | 'horizontal') => {
      if (type === 'vertical') {
        return orientation === 'vertical' || orientation === 'both' ? {} : { display: 'none' };
      }
      return orientation === 'horizontal' || orientation === 'both' ? {} : { display: 'none' };
    };

    return (
      <div ref={ref} className={cn('relative overflow-hidden', className)} {...props}>
        <div
          ref={scrollRef}
          className={cn(
            'h-full w-full overflow-auto scrollbar-thin scrollbar-track-transparent',
            'scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400',
            // Custom scrollbar styles for webkit browsers
            '[&::-webkit-scrollbar]:w-2',
            '[&::-webkit-scrollbar]:h-2',
            '[&::-webkit-scrollbar-track]:bg-transparent',
            '[&::-webkit-scrollbar-thumb]:bg-gray-300',
            '[&::-webkit-scrollbar-thumb]:rounded-full',
            '[&::-webkit-scrollbar-thumb:hover]:bg-gray-400',
            // Hide scrollbar when not scrolling (for non-webkit browsers)
            !isScrolling && 'scrollbar-thumb-transparent'
          )}
        >
          {children}
        </div>

        {/* Custom scrollbar for vertical */}
        {showVerticalBar && (
          <div
            className={cn(
              'absolute right-0 top-0 z-10 w-2 h-full transition-opacity duration-300',
              isScrolling ? 'opacity-100' : 'opacity-0 hover:opacity-100',
              trackClassName
            )}
            style={getScrollbarStyle('vertical')}
          >
            <div
              className={cn(
                'w-full bg-gray-300 rounded-full transition-colors hover:bg-gray-400',
                thumbClassName
              )}
              style={{
                height: '20%', // Simplified for now
                transform: 'translateY(0)', // Simplified for now
              }}
            />
          </div>
        )}

        {/* Custom scrollbar for horizontal */}
        {showHorizontalBar && (
          <div
            className={cn(
              'absolute bottom-0 left-0 z-10 h-2 w-full transition-opacity duration-300',
              isScrolling ? 'opacity-100' : 'opacity-0 hover:opacity-100',
              trackClassName
            )}
            style={getScrollbarStyle('horizontal')}
          >
            <div
              className={cn(
                'h-full bg-gray-300 rounded-full transition-colors hover:bg-gray-400',
                thumbClassName
              )}
              style={{
                width: '20%', // Simplified for now
                transform: 'translateX(0)', // Simplified for now
              }}
            />
          </div>
        )}
      </div>
    );
  }
);

ScrollArea.displayName = 'ScrollArea';

export type { ScrollAreaProps };
